import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface ReaderAlsoLikedBook {
  id: string;
  title: string;
  authors: string[] | null;
  cover_url: string | null;
  cover_cache_url: string | null;
  isbn13: string | null;
  isbn10: string | null;
  open_library_id: string | null;
  likeCount: number;
}

export function useReadersAlsoLiked(bookId: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['readers-also-liked', bookId, user?.id],
    queryFn: async (): Promise<ReaderAlsoLikedBook[]> => {
      if (!bookId) return [];

      // Step 1: Find users who rated this book 4+ stars with public privacy
      const { data: highRaters, error: ratersError } = await supabase
        .from('user_books')
        .select('user_id')
        .eq('book_id', bookId)
        .gte('rating_star', 4)
        .eq('privacy', 'public')
        .limit(50);

      if (ratersError || !highRaters?.length) {
        console.log('No high raters found for book:', bookId);
        return [];
      }

      const raterIds = highRaters.map(r => r.user_id);

      // Step 2: Find other books these users also rated 4+ stars
      const { data: otherBooks, error: booksError } = await supabase
        .from('user_books')
        .select(`
          book_id,
          books:books!user_books_book_id_fkey (
            id,
            title,
            authors,
            cover_url,
            cover_cache_url,
            isbn13,
            isbn10,
            open_library_id
          )
        `)
        .in('user_id', raterIds)
        .gte('rating_star', 4)
        .eq('privacy', 'public')
        .neq('book_id', bookId);

      if (booksError || !otherBooks?.length) {
        console.log('No other liked books found');
        return [];
      }

      // Step 3: Get current user's books to exclude
      let userBookIds: string[] = [];
      if (user?.id) {
        const { data: userBooks } = await supabase
          .from('user_books')
          .select('book_id')
          .eq('user_id', user.id);
        
        userBookIds = userBooks?.map(ub => ub.book_id) || [];
      }

      // Step 4: Count occurrences and filter
      const bookCounts = new Map<string, { book: ReaderAlsoLikedBook; count: number }>();

      for (const item of otherBooks) {
        const book = item.books as unknown as {
          id: string;
          title: string;
          authors: string[] | null;
          cover_url: string | null;
          cover_cache_url: string | null;
          isbn13: string | null;
          isbn10: string | null;
          open_library_id: string | null;
        };

        if (!book || !book.id) continue;
        if (userBookIds.includes(book.id)) continue;

        const existing = bookCounts.get(book.id);
        if (existing) {
          existing.count++;
        } else {
          bookCounts.set(book.id, {
            book: {
              id: book.id,
              title: book.title,
              authors: book.authors,
              cover_url: book.cover_url,
              cover_cache_url: book.cover_cache_url,
              isbn13: book.isbn13,
              isbn10: book.isbn10,
              open_library_id: book.open_library_id,
              likeCount: 1
            },
            count: 1
          });
        }
      }

      // Step 5: Sort by popularity and return top 8
      const sortedBooks = Array.from(bookCounts.values())
        .sort((a, b) => b.count - a.count)
        .slice(0, 8)
        .map(item => ({ ...item.book, likeCount: item.count }));

      return sortedBooks;
    },
    enabled: !!bookId,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}
